import { useEffect, useState, useCallback } from 'react'
import { Activity, Plus, X, Trash2, RefreshCw, TrendingDown } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SLO {
  id: number
  name: string
  service: string
  sli_target_pct: number
  observation_window_days: number
  current_actual_pct: number | null
  last_observed_at: string | null
  notes: string
  burn: { tone: string; label: string; budget_used_pct: number | null }
}

const TONE: Record<string, string> = {
  rose:    'bg-rose/15 ring-rose/40 text-rose',
  amber:   'bg-amber/15 ring-amber/40 text-amber-light',
  emerald: 'bg-emerald/15 ring-emerald/40 text-emerald',
  blue:    'bg-blue/15 ring-blue/40 text-blue-light',
}

export default function SloPage() {
  const [slos, setSlos] = useState<SLO[]>([])
  const [summary, setSummary] = useState<any>({})
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ name: '', service: '', sli_target_pct: 99.9, observation_window_days: 30, notes: '' })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/sre/slos')
      setSlos(r.data?.slos || [])
      setSummary(r.data?.summary || {})
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.name) return
    await api.post('/api/sre/slos', form)
    setShowForm(false)
    setForm({ name: '', service: '', sli_target_pct: 99.9, observation_window_days: 30, notes: '' })
    fetchAll()
  }

  const remove = async (id: number) => {
    if (!confirm('Delete SLO?')) return
    await api.delete(`/api/sre/slos/${id}`)
    fetchAll()
  }

  const observe = async (id: number) => {
    const v = prompt('Observed SLI % (e.g. 99.95)')
    if (!v) return
    const n = parseFloat(v)
    if (Number.isNaN(n)) return
    await api.post(`/api/sre/slos/${id}/observation`, { actual_pct: n })
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Activity size={24} className="text-blue-light" /> SLO Burn Board
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Per-service SLO targets vs observed SLI. Push observations from Prometheus / synthetic checks via the API.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Add SLO</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total SLOs" value={summary.total || 0} tone="blue" />
        <Tile label="Healthy" value={summary.healthy || 0} tone="emerald" />
        <Tile label="Burning" value={summary.burning || 0} tone="amber" pulse={(summary.burning || 0) > 0} />
        <Tile label="Budget exhausted" value={summary.exhausted || 0} tone="rose" pulse={(summary.exhausted || 0) > 0} />
      </div>

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Add SLO</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder='SLO name e.g. "API 99.9% availability"' className="input py-1.5 md:col-span-2" />
            <input value={form.service} onChange={e => setForm({...form, service: e.target.value})} placeholder="Service" className="input py-1.5" />
            <input type="number" step="0.001" value={form.sli_target_pct} onChange={e => setForm({...form, sli_target_pct: parseFloat(e.target.value)})} placeholder="Target % (99.9)" className="input py-1.5" />
            <input type="number" value={form.observation_window_days} onChange={e => setForm({...form, observation_window_days: parseInt(e.target.value, 10)})} placeholder="Window days" className="input py-1.5" />
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notes" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      {slos.length === 0 ? (
        <p className="card p-6 text-center text-text-muted text-[11px] italic">
          No SLOs defined. Add one above, or POST observations to <code className="font-mono">/api/sre/slos/{`{id}`}/observation</code>.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {slos.map(s => (
            <div key={s.id} className={clsx('rounded-xl p-4 ring-2', TONE[s.burn.tone])}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-semibold text-text-primary">{s.name}</h3>
                <button type="button" onClick={() => remove(s.id)} className="text-text-muted hover:text-rose"><Trash2 size={10} /></button>
              </div>
              {s.service && <div className="text-[10px] text-text-muted">{s.service}</div>}
              <div className="mt-2">
                <div className="text-2xl font-black font-mono">
                  {s.current_actual_pct?.toFixed(3) ?? '—'}<span className="text-xs opacity-70">%</span>
                </div>
                <div className="text-[10px] mt-0.5 opacity-80">
                  Target: <span className="font-mono">{s.sli_target_pct.toFixed(3)}%</span>
                </div>
              </div>
              <div className="mt-2 text-[10px] uppercase font-bold tracking-wider">{s.burn.label}</div>
              {s.burn.budget_used_pct != null && (
                <div className="mt-1">
                  <div className="text-[10px] opacity-80">Budget used: {s.burn.budget_used_pct}%</div>
                  <div className="h-1 bg-surface-raised rounded mt-1 overflow-hidden">
                    <div className="h-full bg-current opacity-50" style={{ width: `${Math.min(100, s.burn.budget_used_pct)}%` }} />
                  </div>
                </div>
              )}
              {s.last_observed_at && (
                <div className="text-[9px] mt-2 opacity-70">last obs {new Date(s.last_observed_at).toLocaleString()}</div>
              )}
              <button type="button" onClick={() => observe(s.id)} className="btn-ghost text-[10px] mt-2 w-full">
                <TrendingDown size={10} /> Push observation
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
